import dayjs from 'dayjs';
import { Formik } from 'formik';
import { capitalize, isEmpty, omit, startCase } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { Alert, Button, Col, Row } from 'react-bootstrap';
import { generatePath, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import { patientRoute, workQueueRoute } from 'App/common/routeLookup';
import UserBranchSwitcher from 'App/components/UserBranchSwitcher/UserBranchSwitcher';
import { useUserContext } from 'App/contexts/UserContext';
import useFetchServerTime from 'App/features/WorkQueue/hooks/useFetchServerTime';
import CompanyService from 'App/services/CompanyService';
import { isoDateTimeFormat } from 'App/services/DateService';
import PESService from 'App/services/PESService';
import {
    createOtherCondition,
    deleteOtherCondition,
    updateOtherCondition,
} from 'App/services/PatientOtherConditionService';
import { disablePatient, enablePatient, updateHighRiskReasons } from 'App/services/PatientService';
import { isExpired } from 'App/utils';
import findRelevantRxFill from 'App/utils/findRelevantRxFill';
import removeNullsFromObject from 'App/utils/removeNullsFromObject';
import CancelButton from 'Shared/form/CancelButton';
import ResetButton from 'Shared/form/ResetButton';
import SubmitButton from 'Shared/form/SubmitButton';
import { queryClient } from 'Shared/queryClient';
import toast from 'Shared/toast';
import {
    otherMedicationsPropType,
    patientInsurancePropType,
    patientPropType,
    patientTherapyPropType,
    rxPropType,
} from 'Shared/types';
import FormButtonGroup from '../styles/FormButtonGroup';
import PageWrapper from '../styles/PageWrapper/PageWrapper';
import AllergiesForm from './components/AllergiesForm';
import ChangeBranch from './components/ChangeBranch';
import EmergencyContactForm from './components/EmergencyContactForm';
import MedicalPlanForm from './components/MedicalPlanForm';
import OtherDiagnosesForm from './components/OtherDiagnosesForm';
import OtherMedicationsForm from './components/OtherMedicationsForm';
import PatientDemographicsForm from './components/PatientDemographicsForm';
import PatientInsuranceForm from './components/PatientInsuranceForm';
import TherapeuticCategoryForm from './components/TherapeuticCategory/TherapeuticCategoryForm';
import WelcomePacketForm from './components/WelcomePacketForm';
import { highRiskOptions, states } from './constants';
import {
    patientHighRiskReasonPropType,
    patientLabelPropType,
    patientMedicationRxPropType,
    patientOtherConditionPropType,
} from './types';
import { submitPatientTherapies } from './utils';
import PatientDemographicsNewUIForm from './components/PatientDemographicsNewUIForm';
import useIsPermitted from 'App/utils/hooks/useIsPermitted';
import { PERMISSION_TYPES } from 'App/utils/isPermitted';

function convertToDropdownOption(input) {
    if (!input) return input;
    return {
        label: input,
        value: input,
    };
}

function normalizeString(value) {
    return String(value || '')
        .trim()
        .toLowerCase();
}

function getOtherMedicationCombinationKey(otherMedication = {}) {
    const text = normalizeString(otherMedication.text);
    const dose = normalizeString(otherMedication.dose);

    // Ignore partially completed rows while checking uniqueness.
    if (!text || !dose) {
        return null;
    }

    return `${text}__${dose}`;
}

const validationSchema = Yup.object().shape({
    zip: Yup.string().matches(/^\d{5}(?:-?\d{4})?$/, 'The zip is not in the correct format'),
    email: Yup.string().email('The email is not in the correct format'),
    mobilePhone: Yup.string().min(10, 'The mobile phone must be 10 digits'),
    homePhone: Yup.string().min(10, 'The home phone must be 10 digits'),
    workPhone: Yup.string().min(10, 'The work phone must be 10 digits'),

    therapeuticCategory: Yup.array().min(1, 'Therapeutic category is required'),

    ecTelephone: Yup.string().min(10, 'The home phone must be 10 digits'),
    ecMobilePhone: Yup.string().min(10, 'The mobile phone must be 10 digits'),
    labels: Yup.array().max(6, 'Cannot add more than six (6) labels'),
    otherMedication: Yup.array().test(
        'unique-other-medication-text-dose',
        'This medication and strength already exist.',
        (otherMedication) => {
            const indexMap = otherMedication.reduce((map, medication, index) => {
                const combinationKey = getOtherMedicationCombinationKey(medication);
                if (combinationKey) {
                    map.set(combinationKey, [...(map.get(combinationKey) || []), index]);
                }
                return map;
            }, new Map());

            const duplicateIndices = Array.from(indexMap.values())
                .filter((indices) => indices.length > 1)
                .flat();

            if (!duplicateIndices.length) {
                return true;
            }

            const duplicateErrors = duplicateIndices.flatMap((index) => [
                new Yup.ValidationError(
                    'This medication and strength already exist.',
                    otherMedication[index].text,
                    `otherMedication[${index}].text`
                ),
                new Yup.ValidationError(
                    'This medication and strength already exist.',
                    otherMedication[index].dose,
                    `otherMedication[${index}].dose`
                ),
            ]);

            return new Yup.ValidationError(duplicateErrors);
        }
    ),
});

async function submitHighRiskReasons(patientId, values) {
    const highRiskReasonStrings = values.highRiskReasons.map((reason) => reason.value);
    await updateHighRiskReasons(patientId, highRiskReasonStrings);
}

async function submitOtherIdentifier(patientId, values) {
    if (!values.otherId) {
        return PESService.deletePatientIdentifier(patientId, 'stm_other_id');
    }
    return PESService.upsertPatientIdentifiers(patientId, { Type: 'stm_other_id', Id: values.otherId });
}

const PatientForm = ({
    existingPatient,
    existingPatientLabels,
    existingPatientHighRiskReasons,
    existingPatientOtherId,
    existingPatientTherapies,
    existingPatientMedicationsRx,
    existingPatientRxList,
    existingPatientReferral,
    existingPatientOtherDiagnoses,
    existingPatientOtherMedications,
    existingPatientInsurance,
}) => {
    const navigate = useNavigate();
    const user = useUserContext();
    const { data: serverTime } = useFetchServerTime();
    const [lockError, setLockError] = useState(false);
    const isAddEditPatientUIEnabled = useIsPermitted(PERMISSION_TYPES.ENABLE_ADD_EDIT_PATIENT_UI);

    function getInitialValues() {
        return {
            requiredOnly: false,

            ...(existingPatient && removeNullsFromObject(existingPatient)),
            firstName: existingPatient?.firstName || '',
            lastName: existingPatient?.lastName || '',
            birthDate: existingPatient?.birthDate
                ? dayjs.utc(existingPatient.birthDate).hour(12).minute(0).second(0).toDate()
                : null,
            gender: convertToDropdownOption(existingPatient?.gender && capitalize(existingPatient?.gender)),
            pronouns: convertToDropdownOption(existingPatient?.pronouns),
            genderIdentity: convertToDropdownOption(existingPatient?.genderIdentity),
            ethnicity: convertToDropdownOption(existingPatient?.ethnicity),

            pregnancyStatus: convertToDropdownOption(existingPatient?.pregnancyStatus),
            lactating: convertToDropdownOption(existingPatient?.lactating),

            city: existingPatient?.city || '',
            state: existingPatient ? states.find((s) => s.value === existingPatient.state) : null,
            zip: existingPatient?.zip || '',
            maritalStatus: convertToDropdownOption(existingPatient?.maritalStatus),
            status: existingPatient?.status ? { ValueName: existingPatient.status } : null,
            deceasedDate: existingPatient?.deceasedDate
                ? dayjs.utc(existingPatient.deceasedDate).hour(12).minute(0).second(0).toDate()
                : null,
            preferredContactMethod: convertToDropdownOption(existingPatient?.preferredContactMethod),
            languageSpoken: convertToDropdownOption(existingPatient?.languageSpoken),

            height: existingPatient?.height ? String(existingPatient.height) : null,
            weight: existingPatient?.weight ? String(existingPatient.weight) : null,

            terminallyIll: convertToDropdownOption(existingPatient?.terminallyIll),
            smokerStatus: convertToDropdownOption(existingPatient?.smokerStatus),

            highRisk: existingPatient ? highRiskOptions.find((o) => o.value === existingPatient.highRisk) : null,
            highRiskReasons: existingPatientHighRiskReasons.map((hrr) => convertToDropdownOption(hrr.reason)),
            labels: existingPatientLabels,
            otherId: existingPatientOtherId,

            therapeuticCategory: [...existingPatientTherapies],
            medications: existingPatientMedicationsRx.filter((patientMed) => !isExpired(patientMed.status.end)),

            rxs: existingPatientRxList.map((rxNumber) => ({
                ...rxNumber,
                rxFill: rxNumber.rxFill.length ? [findRelevantRxFill(rxNumber.rxFill)] : [],
            })),
            patientReferral: existingPatientReferral,

            noKnownAllergies: existingPatient?.noKnownAllergies === '1',
            otherAllergy: existingPatient?.otherAllergy?.length ? existingPatient.otherAllergy.split(',') : [],

            noKnownMedicalConditions: existingPatient?.noKnownMedicalConditions === '1',
            otherCondition: [...existingPatientOtherDiagnoses],
            otherMedication: [...existingPatientOtherMedications],

            patientInsurance: existingPatientInsurance
                .filter((insurance) => !insurance.disabledOn)
                .map((insurance) => ({
                    ...insurance,
                    coverageType: convertToDropdownOption(insurance.coverageType),
                    payerSegment: convertToDropdownOption(insurance.payerSegment),
                    isIntegratedHighDeductable: convertToDropdownOption(insurance.isIntegratedHighDeductable),
                    coverageEffectiveDate:
                        insurance.coverageEffectiveDate &&
                        dayjs.utc(insurance.coverageEffectiveDate).hour(12).minute(0).second(0).toDate(),
                    coverageEndDate:
                        insurance.coverageEndDate &&
                        dayjs.utc(insurance.coverageEndDate).hour(12).minute(0).second(0).toDate(),
                    isPharmacyBenefitsCarveOutFromMedical: convertToDropdownOption(
                        insurance.isPharmacyBenefitsCarveOutFromMedical
                    ),
                    disableInsurancePlan: false,
                })),

            ecRelationship: existingPatient?.ecRelationship ? { valueName: existingPatient.ecRelationship } : null,

            pccSendEmail: false,
            pccId: null,

            welcomePacket: existingPatient?.welcomePacket || false,
            welcomePacketSent:
                existingPatient?.welcomePacketSent &&
                dayjs.utc(existingPatient?.welcomePacketSent).hour(12).minute(0).second(0).toDate(),
            welcomePacketReturned:
                existingPatient?.welcomePacketReturned &&
                dayjs.utc(existingPatient?.welcomePacketReturned).hour(12).minute(0).second(0).toDate(),
        };
    }

    async function isPatientLocked() {
        if (!existingPatient?.id) {
            return false;
        }
        try {
            await CompanyService.lockForUpdate({
                openedOn: serverTime?.utcMillisec,
                resourceType: 'patientId',
                resourceId: existingPatient.id,
            });
        } catch (err) {
            if (err.response.status === 409) {
                setLockError(true);
            }
            return true;
        }
        return false;
    }

    async function savePatient(values) {
        const patient = await submitPatient(values);

        const results = await Promise.allSettled([
            submitHighRiskReasons(patient.id, values),
            submitPatientLabels(patient.id, values),
            submitOtherIdentifier(patient.id, values),
            submitPatientTherapies(patient.id, values),
            submitPatientICD10s(patient.id, values),
            submitPatientReferral(patient.id, values),
            submitPatientOtherCondition(patient.id, values),
            submitPatientOtherMedication(patient.id, values),
            submitPatientInsurance(patient.id, values),
        ]);

        /**
         * Prescriptions needs to complete AFTER the others, but they also need
         * to to be included in the results as well as completed before redirect
         * at the end of the function.
         */
        const prescriptionsResult = await submitPatientPrescriptions(patient.id, values)
            .then((value) => ({ status: 'fulfilled', value }))
            .catch((reason) => ({ status: 'rejected', reason }));

        results.push(prescriptionsResult);

        const hasFailures = results.some((r) => r.status === 'rejected');

        if (hasFailures) {
            toast.error('An error occurred on creation of additional details. Navigating to the patient profile.');
            goToPatientProfile(patient.id);
        }

        await queryClient.invalidateQueries(['patient', patient.id]);
        return patient;
    }

    async function submitPatient(values) {
        const isMale = values.gender.value === 'Male';
        const payload = {
            firstName: values.firstName,
            lastName: values.lastName,
            birthDate: values.birthDate,
            gender: values.gender.value,
            deceasedDate: values.deceasedDate,

            preferredName: values.preferredName,
            pronouns: values.pronouns?.value,
            genderIdentity: values.genderIdentity?.value,

            middleName: values.middleName,
            suffix: values.suffix,
            ethnicity: values.ethnicity?.value,

            addressLine1: values.addressLine1,
            addressLine2: values.addressLine2,
            pregnancyStatus: isMale ? null : values.pregnancyStatus?.value,
            lactating: isMale ? null : values.lactating?.value,

            city: values.city,
            state: values.state.value,
            maritalStatus: values.maritalStatus?.value,
            status: values.status?.ValueName,

            zip: values.zip,
            country: values.country,
            preferredContactMethod: values.preferredContactMethod?.value,
            languageSpoken: values.languageSpoken?.value,

            height: values.height ? String(values.height) : '',
            weight: values.weight ? String(values.weight) : '',
            email: values.email,
            mobilePhone: values.mobilePhone,

            terminallyIll: values.terminallyIll?.value,
            smokerStatus: values.smokerStatus?.value,
            homePhone: values.homePhone,
            workPhone: values.workPhone,

            highRisk: values.highRisk?.value,
            pharmacy: values.pharmacy,
            ssnLastFour: values.ssnLastFour,
            externalId: values.externalId,

            medicalPlan: values.medicalPlan,
            remoteMedicalPlanId: values.remoteMedicalPlanId,
            pbm: values.pbm,
            remotePbmId: values.remotePbmId,

            noKnownAllergies: values.noKnownAllergies ? '1' : '0',
            allergy: values.noKnownAllergies ? '' : values.allergy,
            otherAllergy: values.noKnownAllergies ? '' : values.otherAllergy.join(','),

            noKnownMedicalConditions: values.noKnownMedicalConditions ? '1' : '0',

            ecFirstName: values.ecFirstName,
            ecLastName: values.ecLastName,
            ecTelephone: values.ecTelephone,
            ecMobilePhone: values.ecMobilePhone,
            ecRelationship: values.ecRelationship?.valueName,

            pccSendEmail: values.pccSendEmail,
            pccId: values.pccSendEmail ? values.pccId?.value : '',

            welcomePacket: values.welcomePacket,
            welcomePacketSent: values.welcomePacket ? values.welcomePacketSent : null,
            welcomePacketReturned: values.welcomePacket ? values.welcomePacketReturned : null,
        };

        if (existingPatient?.id) {
            return PESService.updatePatient(existingPatient.id, payload);
        }
        return PESService.createPatient(payload);
    }

    async function submitPatientLabels(patientId, values) {
        const labelsToDelete = existingPatientLabels.filter(
            (epl) => values.labels.findIndex((label) => label.id === epl.id) === -1
        );
        const labelsToAdd = values.labels.filter(
            (label) => existingPatientLabels.findIndex((epl) => epl.id === label.id) === -1
        );
        const deleteRequests = labelsToDelete.map((label) => PESService.deletePatientLabel(patientId, label.id));
        const addRequests = labelsToAdd.map((label) => PESService.addPatientLabel(patientId, label.label));
        const promises = [...deleteRequests, ...addRequests];
        await Promise.all(promises);
    }

    async function submitPatientICD10s(patientId, values) {
        return Promise.all(
            values.therapeuticCategory.map((therapy) =>
                PESService.addRemovePatientICD10(patientId, therapy.id, {
                    patientICD10: therapy.icd10 || [],
                })
            )
        );
    }

    async function submitPatientPrescriptions(patientId, values) {
        for (const rx of values.rxs) {
            const res = await PESService.addPatientPrescriptions(patientId, [
                {
                    medicationId: rx.medication.id,
                    rxNumbers: [
                        {
                            ...omit(rx, [
                                'medication',
                                'rxFill',
                                'dispensedProductDescription',
                                'createdOn',
                                'modifiedOn',
                            ]),
                            physician: rx.physician && {
                                id: rx.physician.id,
                            },
                        },
                    ],
                },
            ]);
            for (const fill of rx.rxFill ?? []) {
                if (fill.id) {
                    await PESService.updateRxFill(patientId, fill.id, fill);
                }

                await PESService.addRxFills(patientId, res[0].patientMedicationId, res[0].id, fill);
            }
        }
    }

    async function submitPatientReferral(patientId, values) {
        if (!existingPatientReferral && values.patientReferral) {
            return PESService.addPatientReferral(patientId).catch((e) => e);
        }
        if (existingPatientReferral && !values.patientReferral) {
            return PESService.deletePatientReferral(patientId).catch((e) => e);
        }
    }

    async function submitPatientOtherCondition(patientId, values) {
        const conditionsToDelete = existingPatientOtherDiagnoses.filter(
            (diagnosis) =>
                values.noKnownMedicalConditions ||
                values.otherCondition.findIndex((condition) => condition.id === diagnosis.id) === -1
        );
        const deleteRequests = conditionsToDelete.map((condition) => deleteOtherCondition(patientId, condition.id));
        const addUpdateRequests = values.otherCondition
            .filter((condition) => !values.noKnownMedicalConditions && !!condition.text)
            .map((condition) => {
                if (condition.id) {
                    return updateOtherCondition(patientId, condition.id, { text: condition.text });
                }
                return createOtherCondition(patientId, condition);
            });
        const promises = [...deleteRequests, ...addUpdateRequests];
        await Promise.all(promises);
    }

    async function submitPatientOtherMedication(patientId, values) {
        const medicationsToDelete = existingPatientOtherMedications.filter(
            (existingMed) => values.otherMedication.findIndex((otherMed) => otherMed.id === existingMed.id) === -1
        );
        const deleteRequests = medicationsToDelete.map((medication) =>
            PESService.deletePatientOtherMedication(patientId, medication.id)
        );
        const addUpdateRequests = values.otherMedication
            .filter((medication) => !isEmpty(medication))
            .map((medication) => {
                if (medication.id) {
                    return PESService.updatePatientOtherMedication(patientId, medication.id, medication);
                }
                return PESService.createPatientOtherMedication(patientId, medication);
            });
        const promises = [...deleteRequests, ...addUpdateRequests];
        await Promise.all(promises);
    }

    async function submitPatientInsurance(patientId, values) {
        const existingMaxPosition = existingPatientInsurance.length
            ? Math.max(...existingPatientInsurance.map((i) => i.position))
            : 0;
        let newPositionCounter = existingMaxPosition;
        const promises = values.patientInsurance.map((insurance) => {
            const insurancePayload = {
                ...insurance,
                position: insurance.position || (newPositionCounter += 1),
                coverageType: insurance.coverageType?.value,
                payerSegment: insurance.payerSegment?.value,
                isIntegratedHighDeductable: insurance.isIntegratedHighDeductable?.value,
                isPharmacyBenefitsCarveOutFromMedical: insurance.isPharmacyBenefitsCarveOutFromMedical?.value,
                ...((insurance.disableInsurancePlan || isExpired(insurance.coverageEndDate)) && {
                    disabledOn: dayjs.utc().format(isoDateTimeFormat),
                    disabledBy: user.id,
                }),
            };
            delete insurancePayload.disabledByUser;
            delete insurancePayload.disableInsurancePlan;
            if (insurance.id) {
                return PESService.updatePatientInsurance(patientId, insurance.id, insurancePayload);
            }
            return PESService.addPatientInsurance(patientId, { insurance: [insurancePayload] });
        });
        await Promise.all(promises);
    }

    async function handleDisablePatient(values) {
        const patient = await savePatient(values);
        await disablePatient(patient.id);
        goToPatientProfile(patient.id);
    }

    async function handleEnablePatient(values) {
        const patient = await savePatient(values);
        await enablePatient(patient.id);
        goToPatientProfile(patient.id);
    }

    function goToPatientProfile(patientId) {
        const redirectUrl = new URLSearchParams(window.location.search).get('redirectUrl');
        const destinationUrl = redirectUrl || generatePath(patientRoute, { patientId });
        navigate(destinationUrl);
    }

    const handleCancelEditPatient = useCallback(() => {
        if (existingPatient?.id) {
            navigate(generatePath(patientRoute, { patientId: existingPatient.id }));
        } else {
            navigate(workQueueRoute);
        }
    }, [existingPatient, navigate]);

    return (
        <div className="container">
            <PageWrapper>
                <Formik
                    initialValues={getInitialValues()}
                    validationSchema={validationSchema}
                    onSubmit={async (values) => {
                        try {
                            if (await isPatientLocked()) {
                                return;
                            }
                            const patient = await savePatient(values);
                            goToPatientProfile(patient.id);
                        } catch (error) {
                            // eslint-disable-next-line no-console
                            console.error(error);
                        }
                    }}
                >
                    {({ handleSubmit, submitCount, errors, values, isValid, validateForm }) => (
                        <form onSubmit={handleSubmit}>
                            {!existingPatient?.id && <ChangeBranch />}
                            {!!existingPatient?.id && <UserBranchSwitcher patient={existingPatient} />}

                            {isAddEditPatientUIEnabled ? <PatientDemographicsNewUIForm /> : <PatientDemographicsForm />}

                            <TherapeuticCategoryForm />

                            <Row>
                                <Col md={6}>
                                    <MedicalPlanForm />
                                </Col>
                                <Col md={6}>
                                    <AllergiesForm />
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <OtherDiagnosesForm />
                                </Col>
                                <Col md={6}>
                                    <OtherMedicationsForm />
                                </Col>
                            </Row>

                            <PatientInsuranceForm validateForm={validateForm} />

                            {!isAddEditPatientUIEnabled && <EmergencyContactForm />}

                            <Row>
                                <Col md={6}>
                                    <WelcomePacketForm />
                                </Col>
                            </Row>

                            {((submitCount > 0 && !isValid) || lockError) && (
                                <Alert variant="danger" style={{ marginTop: 20 }}>
                                    <ul>
                                        {Object.keys(errors).map((key) => (
                                            <li key={key}>
                                                {startCase(key)} is <strong>required</strong>.
                                            </li>
                                        ))}
                                        {lockError && (
                                            <li>
                                                This page has been modified since it was initially loaded. Your changes{' '}
                                                <strong>cannot</strong> be saved. Please reload the page to get the
                                                latest changes.
                                            </li>
                                        )}
                                    </ul>
                                </Alert>
                            )}

                            <Row style={{ marginTop: 25 }}>
                                <Col md={6}>
                                    {existingPatient?.id ? (
                                        <>
                                            This patient is part of <strong>{user.active_branch.Name}</strong> branch.
                                        </>
                                    ) : (
                                        <>
                                            This patient will be added to branch{' '}
                                            <strong>{user.active_branch.Name}</strong>.
                                        </>
                                    )}
                                </Col>

                                <Col md={6}>
                                    <FormButtonGroup>
                                        {existingPatient?.id &&
                                            (!existingPatient.disabledBy ? (
                                                <Button variant="danger" onClick={() => handleDisablePatient(values)}>
                                                    Disable Patient
                                                </Button>
                                            ) : (
                                                <Button variant="success" onClick={() => handleEnablePatient(values)}>
                                                    Enable Patient
                                                </Button>
                                            ))}
                                        <CancelButton onClick={handleCancelEditPatient} />
                                        <ResetButton>Reset</ResetButton>
                                        <SubmitButton ignoreDirty ignoreValid>
                                            {existingPatient?.id ? 'Save Patient' : 'Add Patient'}
                                        </SubmitButton>
                                    </FormButtonGroup>
                                </Col>
                            </Row>
                        </form>
                    )}
                </Formik>
            </PageWrapper>
        </div>
    );
};

PatientForm.propTypes = {
    existingPatient: patientPropType,
    existingPatientLabels: PropTypes.arrayOf(patientLabelPropType),
    existingPatientHighRiskReasons: PropTypes.arrayOf(patientHighRiskReasonPropType),
    existingPatientOtherId: PropTypes.string,
    existingPatientTherapies: PropTypes.arrayOf(patientTherapyPropType),
    existingPatientMedicationsRx: PropTypes.arrayOf(patientMedicationRxPropType),
    existingPatientRxList: PropTypes.arrayOf(rxPropType),
    existingPatientReferral: PropTypes.bool,
    existingPatientOtherDiagnoses: PropTypes.arrayOf(patientOtherConditionPropType),
    existingPatientOtherMedications: otherMedicationsPropType,
    existingPatientInsurance: PropTypes.arrayOf(patientInsurancePropType),
};
PatientForm.defaultProps = {
    existingPatient: null,
    existingPatientLabels: [],
    existingPatientHighRiskReasons: [],
    existingPatientOtherId: null,
    existingPatientTherapies: [],
    existingPatientMedicationsRx: [],
    existingPatientRxList: [],
    existingPatientReferral: false,
    existingPatientOtherDiagnoses: [],
    existingPatientOtherMedications: [{}],
    existingPatientInsurance: [],
};

export default PatientForm;
